import {Color, Graphic, GraphicName} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {FONT_FAMILY_PROXIMA, FONT_PROXIMA_14, FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {BLUE, GRAY, GRAY_LIGHTER, GRAY_SEMIDARK, NEAR_BLACK} from 'ui-library/Colors';

type WorksheetGraphic = Extract<GraphicName, 'worksheet' | 'worksheets'>;
export interface MultiBatchButtonProps {
  graphic: WorksheetGraphic;
  headerText: string;
  description: string;
  active: boolean;
  includeRadioButton: boolean;
  onSelect?: () => void;
  isDisabled?: boolean;
  dataTestAttrs: DataTestAttrs;
}

export default function MultiBatchButton({
  graphic,
  headerText,
  description,
  active,
  includeRadioButton,
  onSelect,
  isDisabled,
  dataTestAttrs,
}: MultiBatchButtonProps): JSX.Element {
  const {width, height} = graphicToDimension[graphic];
  return (
    <div
      className={css(
        styles.toggleButton,
        active && styles.activeButton,
        !isDisabled && styles.enabledToggleButton
      )}
      onClick={isDisabled ? undefined : onSelect}
      aria-disabled={isDisabled}
      {...getTestProps(dataTestAttrs)}
    >
      <div className={css(styles.modeGraphicContainer)}>
        <Graphic
          name={graphic}
          className={css(styles.modeGraphic, active && styles.modeGraphicActive)}
          style={{width, minWidth: width, height, minHeight: height}}
        />
      </div>
      <div className={css(styles.modeText)}>
        <div className={css(styles.modeHeader)}>{headerText}</div>
        <div className={css(styles.modeDescription)}>{description}</div>
      </div>
      {includeRadioButton && (
        <input type="radio" className={css(styles.radioButton)} checked={active} readOnly={true} />
      )}
    </div>
  );
}

const graphicToDimension: Record<WorksheetGraphic, {width: number; height: number}> = {
  worksheet: {width: 30, height: 34},
  worksheets: {width: 49, height: 34},
};

const styles = StyleSheet.create({
  toggleButton: {
    width: '100%',
    flex: '1 0 74px',
    padding: '12px 16px 12px 16px',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 16,
    border: `1px solid ${GRAY}`,
    borderRadius: 4,
    color: NEAR_BLACK,
  },
  enabledToggleButton: {
    ':hover': {
      backgroundColor: GRAY_LIGHTER,
    },
    cursor: 'pointer',
  },
  activeButton: {
    // This property is set in a pseudo-selector, so it has the same level of specificity
    // as the property in toggleButton:hover that it's overriding.
    ':hover': {
      backgroundColor: Color.BLUE_50,
    },
    backgroundColor: Color.BLUE_50,
    borderColor: BLUE,
    color: BLUE,
  },
  radioButton: {
    // Hide the default radio button, so we can supply a custom styled one
    appearance: 'none',
    margin: 0,
    width: 16,
    height: 16,
    flex: '0 0 auto',
    border: `1px solid ${GRAY}`,
    borderRadius: '50%',
    // Prevent any affordance when clicking on the radio button, itself
    ':focus': {
      outline: 'none',
    },
    ':checked': {
      backgroundColor: BLUE,
      borderColor: BLUE,
      padding: 2,
      backgroundClip: 'content-box',
    },
  },
  modeGraphic: {
    color: GRAY_SEMIDARK,
  },
  modeGraphicActive: {
    color: BLUE,
  },
  modeGraphicContainer: {
    height: '100%',
    width: 50,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flex: '0 0 auto',
  },
  modeText: {
    flex: '1 1 auto',
  },
  modeHeader: {
    fontFamily: FONT_FAMILY_PROXIMA,
    ...FONT_PROXIMA_14,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    lineHeight: '24px',
  },
  modeDescription: {
    fontFamily: FONT_FAMILY_PROXIMA,
    fontSize: '12px',
    lineHeight: '14px',
  },
});
